import type { ServiceStatus } from "../api/types";
import { Empty, Panel } from "./Panel";

interface Props {
  status: ServiceStatus | undefined;
  error: Error | null;
  loading: boolean;
}

export function StatusPanel({ status, error, loading }: Props) {
  return (
    <Panel title="Состояние системы" subtitle={status ? `веха ${status.milestone}` : undefined}>
      {loading && <Empty>загрузка…</Empty>}
      {error && (
        <p className="text-sm text-[var(--color-sell)]">
          web_api недоступен: {error.message}
        </p>
      )}
      {status && (
        <>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              label="Вкладок с UI"
              value={String(status.ui_connections)}
              hint="Открытых сокетов интерфейса. Это НЕ число людей: токен один на всех, и каждая вкладка держит своё соединение — четыре вкладки одного оператора дают четыре."
            />
            <Stat
              label="Рынков в работе"
              value={status.tracked_markets === null ? "нет данных" : String(status.tracked_markets)}
              tone={status.tracked_markets === null ? "warn" : "neutral"}
            />
            <Stat
              label="Ключи биржи"
              value={
                status.polymarket_configured === null
                  ? "неизвестно"
                  : status.polymarket_configured
                    ? "заданы"
                    : "не заданы"
              }
              tone={status.polymarket_configured ? "ok" : "warn"}
            />
            <Stat
              label="Аварийный стоп"
              value={
                status.kill_switch === null
                  ? "неизвестно"
                  : status.kill_switch
                    ? "включён"
                    : "снят"
              }
              tone={status.kill_switch === false ? "ok" : "warn"}
            />
            <Stat
              label="Торговля"
              value={status.trading ? "идёт" : "остановлена"}
              tone={status.trading ? "ok" : "warn"}
            />
          </dl>

          {!status.trader_reachable && (
            <p className="mt-4 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
              Трейдер недоступен: состояние ключей биржи и аварийного стопа
              неизвестно. Проверьте <code className="num">docker compose ps polymarket_trader</code>{" "}
              и его логи.
            </p>
          )}
          {status.kill_switch && (
            <p className="mt-4 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
              Торговля заблокирована аварийным стопом: ни один ордер не уйдёт на биржу.{" "}
              {status.kill_switch_env
                ? // Стоп из окружения кнопкой не снимается — без этого различия
                  // оператор жал бы «снять» и не понимал, почему ничего не происходит.
                  "Источник — переменная RISK_KILL_SWITCH у трейдера; правьте .env и перезапускайте его."
                : "Источник — кнопка в интерфейсе; снимите её в шапке."}
            </p>
          )}
          {status.polymarket_configured === false && (
            <p className="mt-2 text-xs text-[var(--color-muted)]">
              Ни один аккаунт набора не загружен: ключи приходят файлом ключей от
              crypto-account, а не через <code className="num">.env</code>. Причины по слотам —
              ноты <code className="num">acct.&lt;слот&gt;.loaded</code>. См. docs/deploy.md.
            </p>
          )}
        </>
      )}
    </Panel>
  );
}

function Stat({
  label,
  value,
  tone = "neutral",
  hint,
}: {
  label: string;
  value: string;
  tone?: "neutral" | "ok" | "warn";
  /** Всплывающая подсказка на подписи. Для показателей, которые легко прочесть
   *  не так, как они считаются. */
  hint?: string;
}) {
  const color =
    tone === "ok" ? "var(--color-buy)" : tone === "warn" ? "#eab308" : "inherit";
  return (
    <div>
      <dt
        className={`text-xs text-[var(--color-muted)] ${hint ? "cursor-help decoration-dotted underline-offset-4 [text-decoration-line:underline]" : ""}`}
        title={hint}
      >
        {label}
      </dt>
      <dd className="num mt-0.5 text-base" style={{ color }}>
        {value}
      </dd>
    </div>
  );
}
